import { CatalogPeriodState } from 'utils/catalog-period';
import { FOCO_SOBRE_AZUL } from 'utils/foco';

import { formatarContagem, formatarData, formatarDataHora, progressoDaJanela } from '../catalogo-modelo';

interface PrazoDaEscolhaProps {
  estado: CatalogPeriodState;
  /** Instante do servidor. */
  agora: number;
  inicio?: Date;
  fim?: Date;
}

const RELOGIO = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

// SPEC_DEVIATION: pílula em `brand-blue-dark` (design `brand-blue`, 4,44:1 com o texto amarelo de 13px).
const PILULA =
  'inline-flex items-center gap-2 rounded-full bg-brand-blue-dark px-3.5 py-1.5 font-display text-[13px] font-bold text-brand-yellow';

const NOTA_DA_LIVELO = (
  <p className="m-0 mt-3 text-[11px] leading-[1.6]">
    Encerrado o prazo de escolha, a concessão de pontos Livelo será realizada automaticamente para quem não tiver registrado
    uma opção.
  </p>
);

const ROTULO = 'font-display text-[14px] font-bold text-brand-blue-dark';

/**
 * Caixa branca do prazo, dentro do hero (CAT-03, CAT-04). O anel de foco fica fora da caixa, sobre
 * o azul do hero: por isso é o branco (4,88:1), e não o azul-escuro (1,83:1). Antes da abertura conta até o início;
 * depois, até o fim, com a barra da janela. As contas usam o relógio do servidor.
 */
export default function PrazoDaEscolha({ estado, agora, inicio, fim }: PrazoDaEscolhaProps) {
  if (estado === 'antes' && inicio) {
    return (
      <div role="region" aria-label="Abertura do catálogo" tabIndex={0} className={`mt-5 rounded-xl bg-brand-white px-[18px] py-4 text-content ${FOCO_SOBRE_AZUL}`}>
        <p className="m-0 flex flex-wrap items-center gap-2.5">
          <span className={ROTULO}>O Catálogo de Experiências abre em:</span>
          {/* Sem `aria-label` nem região viva: o nome do relógio é o próprio valor (VIG-09). */}
          <span role="timer" className={PILULA}>
            {RELOGIO}
            {formatarContagem(inicio.getTime() - agora)}
          </span>
        </p>
        <p className="m-0 mt-3 text-[12px] leading-[1.6]">A escolha das experiências começa em {formatarDataHora(inicio)}.</p>
      </div>
    );
  }

  if (estado === 'encerrado' && fim) {
    // Passado o prazo, a vitrine fica só para consulta: o aviso toma o lugar da contagem e da barra (TRC-19).
    return (
      <div role="region" aria-label="Prazo de escolha" tabIndex={0} className={`mt-5 rounded-xl bg-brand-white px-[18px] py-4 text-content ${FOCO_SOBRE_AZUL}`}>
        <p className="m-0 font-display text-[14px] font-bold leading-[1.5] text-brand-blue-dark">
          O prazo de escolha deste ciclo terminou em {formatarData(fim)}. As experiências abaixo ficam apenas para consulta.
        </p>
        {NOTA_DA_LIVELO}
      </div>
    );
  }

  const progresso = inicio && fim ? progressoDaJanela(agora, inicio, fim) : undefined;

  return (
    <div role="region" aria-label="Prazo de escolha" tabIndex={0} className={`mt-5 rounded-xl bg-brand-white px-[18px] py-4 text-content ${FOCO_SOBRE_AZUL}`}>
      <p className="m-0 mb-3 flex flex-wrap items-center gap-2.5">
        <span className={ROTULO}>A sua escolha deve ser finalizada em:</span>
        <span role="timer" aria-label="Contador de tempo restante para escolha da experiência" className={PILULA}>
          {RELOGIO}
          {fim ? formatarContagem(fim.getTime() - agora) : 'Carregando...'}
        </span>
      </p>
      {inicio && fim && typeof progresso === 'number' && (
        <div className="flex items-center gap-3">
          <span className="whitespace-nowrap text-[11px] text-content-soft">De {formatarData(inicio)}</span>
          <div
            role="progressbar"
            aria-label="Tempo decorrido do prazo de escolha"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progresso)}
            className="h-2.5 flex-1 overflow-hidden rounded-full bg-[rgba(70,94,255,0.14)]"
          >
            <div className="h-full rounded-full bg-brand-pink" style={{ width: `${progresso.toFixed(2)}%` }} />
          </div>
          <span className="whitespace-nowrap text-[11px] text-content-soft">Até {formatarData(fim)}</span>
        </div>
      )}
      {NOTA_DA_LIVELO}
    </div>
  );
}
